import { useEffect, useRef, useState } from 'react'
import { ChevronRight, ExternalLink, X } from 'lucide-react'
import ImageLightbox from '../../components/ImageLightbox'
import { useI18n } from '../../i18n/useI18n'
import type { StampSlot } from '../../services/api'
import SlotPrLabel from './SlotPrLabel'
import { isExternalSlotLink, recordSlotImpression, useOpenSlot } from './slotOpen'

/**
 * #2492 PR 枠の「ポップアップ」と「下部に固定」。
 *
 * 今までの枠 (イベント上部 / 企画の下) は**イベントのカードの中**にあり、カードは既定で
 * 閉じている。開かなければ見えないので、出稿してもらっても見てもらえなかった。
 * この 2 つはカードの外 (スタンプ帳の画面全体) に出す。
 *
 * - ポップアップ: 画面を開いたときに手前に出す。**閉じるボタン必須**。1 端末 1 日 1 回
 *   (同じイベントに複数あれば、日ごとに次の枠)。**縦横比を問わない** — 出稿側に画像の形を
 *   合わせてもらうのは負担なので、チラシ (縦長) もそのまま貼れる位置を 1 つ持つ
 * - 下部に固定: 画面下に出し続ける。閉じるボタンは付けない (閉じられると固定の意味が無い)。
 *   帯の高さが決まっているので**横長の画像だけ** (運営画面で縦長を貼ると警告する)
 *
 * 計測は既存の枠と同じ (#1997): 表示は**実際に出たとき**に 1 回、押したら 1 回。
 * **リハーサル中は数えない** (運営の確認ぶんを出稿元の数字と #2491 の目標回数に混ぜない)。
 * 押したときの動きと PR 表記はカードの中の枠と共通 (slotOpen.ts / SlotPrLabel.tsx)。
 * 広告 (type=ad) は「PR」表記を外さない (景品表示法)。
 */

const POPUP_KEY = 'yokoso.stampSlotPopup.v1'
/** 画面が描かれてから出す。読み込み中の一瞬に被せると、何の画面か分からないまま閉じられる */
const POPUP_DELAY_MS = 600
/** 下部の帯が複数あるときの切り替え間隔 (カルーセルと同じ) */
const BOTTOM_ROTATE_MS = 6000

/**
 * この画面を開いている間 (SPA の中) に出したイベント。**再読み込みまでは出し直さない** —
 * ログインの復元で context を取り直すと部品が作り直され、リハーサル中 (毎回出す) は
 * 2 回続けて出てしまう。
 */
const shownThisPage = new Set<string>()

/**
 * 下部の帯で表示を数えた枠。**この画面を開いている間 1 枠 1 回。** 部品の中に持つと、
 * タブの切り替えやログインの復元による取り直しで帯が作り直されるたびに数え直していた
 * (出稿元に返す数字と #2491 の目標回数が水増しになる)。
 */
const bottomCountedThisPage = new Set<string>()

type PopupLog = Record<string, { day: string; n: number }>

function readPopupLog(): PopupLog {
  try {
    const raw = localStorage.getItem(POPUP_KEY)
    return raw ? (JSON.parse(raw) as PopupLog) : {}
  } catch {
    return {}
  }
}

function writePopupLog(log: PopupLog): void {
  try {
    localStorage.setItem(POPUP_KEY, JSON.stringify(log))
  } catch {
    // 保存できない端末 (プライベートブラウズ等) は毎回出る。出ないよりはよい
  }
}

/** 端末の日付 (YYYY-MM-DD)。「1 日 1 回」は見ている人の暦で区切る */
function today(): string {
  return new Date().toLocaleDateString('sv-SE')
}

/**
 * ポップアップ。`groupId` (イベント) ごとに 1 日 1 回。
 *
 * どの枠を出すかは「その端末でこのイベントのポップアップを何日出したか」で回す。
 * サーバは目標回数に届いていない枠を先頭に返す (#2491) ので、初めての人には
 * いちばん足りていない枠が出る。
 *
 * **リハーサル中は毎回出す** (再読み込みのたびに)。運営が見え方を確かめる場面で、
 * 一度見たら翌日まで出ないと確かめようがない。
 */
export function SlotPopup({
  groupId,
  slots,
  rehearsal,
}: {
  groupId: string
  slots: StampSlot[]
  rehearsal: boolean
}) {
  const { t } = useI18n()
  const [slot, setSlot] = useState<StampSlot | null>(null)
  const countedRef = useRef(false)
  const track = !rehearsal
  const { open, viewing, closeViewing } = useOpenSlot(track)
  const lightbox = viewing ? <ImageLightbox src={viewing} onClose={closeViewing} /> : null

  useEffect(() => {
    if (slots.length === 0 || shownThisPage.has(groupId)) return
    const log = readPopupLog()
    const entry = log[groupId]
    if (!rehearsal && entry?.day === today()) return
    const n = entry?.n ?? 0
    const picked = slots[n % slots.length]
    // **画像を読み込んでから出す。** 先に出すと、帯だけの小さな枠が出てから画像の高さに
    // 伸びる (一瞬「詳しくみる」だけの白い帯に見える)。読み込めなくても出す — 押せる状態で
    // 置かれている以上「出ている」扱い (既存の枠の計測と同じ)
    let cancelled = false
    let timer: number | undefined
    const startedAt = Date.now()
    const show = () => {
      if (cancelled) return
      timer = window.setTimeout(() => {
        if (cancelled) return
        shownThisPage.add(groupId)
        writePopupLog({ ...log, [groupId]: { day: today(), n: n + 1 } })
        setSlot(picked)
      }, Math.max(0, POPUP_DELAY_MS - (Date.now() - startedAt)))
    }
    const preload = new Image()
    preload.onload = show
    preload.onerror = show
    preload.src = picked.imageUrl
    return () => {
      cancelled = true
      if (timer !== undefined) window.clearTimeout(timer)
    }
    // 出すかどうかは開いたときに 1 回だけ決める (枠の並びが後から変わっても差し替えない)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId])

  useEffect(() => {
    if (!slot) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSlot(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [slot])

  if (!slot) return lightbox

  /** 画像の結果が出たら表示に数える (読み込み中の枠は何も見えていない)。失敗も「出ている」側 */
  const countImpression = () => {
    if (countedRef.current) return
    countedRef.current = true
    recordSlotImpression(slot, track)
  }
  const external = isExternalSlotLink(slot.linkUrl)

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 px-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label={t('stampRally.slotPopupLabel')}
      data-testid="slot-popup"
      onClick={() => setSlot(null)}
    >
      <div
        className="relative w-full max-w-[400px] overflow-hidden rounded-2xl bg-white shadow-xl animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {slot.type === 'ad' && <SlotPrLabel />}
        <button
          onClick={() => setSlot(null)}
          aria-label={t('stampRally.slotPopupClose')}
          className="absolute right-2 top-2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white tap-effect"
        >
          <X size={20} />
        </button>
        <button
          onClick={() => {
            countImpression()
            if (slot.linkUrl) {
              setSlot(null)
              open(slot)
            }
          }}
          className="block w-full"
          // 飛び先の無いポップアップは画像そのものが中身。押しても何も起きないので押せる見た目にしない
          disabled={!slot.linkUrl}
        >
          <img
            src={slot.imageUrl}
            alt=""
            className="mx-auto max-h-[70vh] w-full bg-white object-contain"
            onLoad={countImpression}
            onError={countImpression}
          />
        </button>
        {slot.linkUrl && (
          <button
            onClick={() => {
              countImpression()
              setSlot(null)
              open(slot)
            }}
            className="flex w-full items-center justify-between gap-2 border-t border-navy/10 px-4 py-3 font-body text-sm font-medium text-navy tap-effect"
          >
            {t('stampRally.slotOpenLink')}
            {external ? <ExternalLink size={16} className="shrink-0" /> : <ChevronRight size={16} className="shrink-0" />}
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * 画面下に固定の帯。**sticky** にする — fixed だと本文の上に浮くだけでレイアウト上の場所を
 * 持たず、スタンプ帳の末尾が帯の下に隠れる (お知らせのポップアップで実際に起きた)。
 * sticky なら画面下に貼り付いたまま、本文の末尾に自分の場所を確保する。
 *
 * 表示回数は**その枠が帯に出て、画像の結果が出たとき**に、この画面を開いている間 1 枠 1 回
 * (切り替わるたびに数えると、開きっぱなしにしただけで延々と増える)。
 */
export function SlotBottomBar({
  slots,
  track,
}: {
  slots: StampSlot[]
  /** 計測を送るか。リハーサル中 (運営の確認) は false */
  track: boolean
}) {
  const { t } = useI18n()
  const [index, setIndex] = useState(0)
  const [settled, setSettled] = useState<Record<string, boolean>>({})
  const { open, viewing, closeViewing } = useOpenSlot(track)
  const reduced =
    typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  const count = slots.length
  const current = count ? slots[index % count] : null

  useEffect(() => {
    if (count < 2 || reduced) return
    const id = window.setInterval(() => setIndex((i) => (i + 1) % count), BOTTOM_ROTATE_MS)
    return () => window.clearInterval(id)
  }, [count, reduced])

  useEffect(() => {
    if (!current || !settled[current.id] || bottomCountedThisPage.has(current.id)) return
    bottomCountedThisPage.add(current.id)
    recordSlotImpression(current, track)
  }, [current, settled, track])

  if (!current) return null

  return (
    <div className="sticky bottom-0 z-40 mt-4 pb-[calc(env(safe-area-inset-bottom)+8px)]" data-testid="slot-bottom-bar">
      <div className="mx-auto w-full max-w-[430px] px-4">
        <div className="relative overflow-hidden rounded-xl bg-white shadow-[0_-2px_12px_rgba(0,0,0,0.12)]">
          {current.type === 'ad' && <SlotPrLabel />}
          <button
            key={current.id}
            onClick={() => {
              if (!bottomCountedThisPage.has(current.id)) {
                // 押されたなら見られている (画像の結果より先に押された場合)
                bottomCountedThisPage.add(current.id)
                recordSlotImpression(current, track)
              }
              open(current)
            }}
            aria-label={current.linkUrl ? t('stampRally.slotOpenLink') : t('stampRally.slotZoom')}
            className="block w-full animate-fade-in"
          >
            <img
              src={current.imageUrl}
              alt=""
              className="h-[72px] w-full bg-white object-contain"
              onLoad={() => setSettled((m) => ({ ...m, [current.id]: true }))}
              onError={() => setSettled((m) => ({ ...m, [current.id]: true }))}
            />
          </button>
          {count > 1 && (
            <div className="pointer-events-none absolute bottom-1 right-2 flex gap-1">
              {slots.map((s, i) => (
                <span
                  key={s.id}
                  className={`h-1.5 rounded-full ${i === index % count ? 'w-3 bg-navy' : 'w-1.5 bg-navy/25'}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {viewing && <ImageLightbox src={viewing} onClose={closeViewing} />}
    </div>
  )
}
